import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Send, FileText, CheckCircle, Clock, ArrowRight, BarChart3, ExternalLink } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const EMPRESAS = [
  { key: 'SPC', label: 'SPC' },
  { key: 'BOA_VISTA', label: 'Boa Vista' },
  { key: 'CENPROT', label: 'Cenprot' },
  { key: 'SERASA', label: 'Serasa' },
];

const COLUMN_CONFIG = {
  ENVIADAS: { title: 'Enviadas', color: 'rgba(249,168,37,0.15)', border: 'rgba(249,168,37,0.3)', icon: Send, iconColor: '#F9A825' },
  AGUARDANDO_BAIXAS: { title: 'Protocoladas (Aguardando)', color: 'rgba(99,203,182,0.08)', border: 'rgba(99,203,182,0.2)', icon: Clock, iconColor: '#63CBB6' },
  BAIXAS_ANDAMENTO: { title: 'Baixas em Andamento', color: 'rgba(99,203,182,0.12)', border: 'rgba(99,203,182,0.25)', icon: ArrowRight, iconColor: '#6FD3C0' },
  CONCLUIDAS: { title: 'Concluidas', color: 'rgba(99,203,182,0.18)', border: 'rgba(99,203,182,0.35)', icon: CheckCircle, iconColor: '#63CBB6' },
};

export default function AdminReturns() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Baixa modal
  const [baixaModal, setBaixaModal] = useState(null); // lista obj
  const [baixaForm, setBaixaForm] = useState({ empresa: 'SPC', observacao: '' });
  const [baixaCases, setBaixaCases] = useState([]);
  const [baixaSelected, setBaixaSelected] = useState([]);
  const [baixaLoading, setBaixaLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.retornoPipeline();
      setData(res.data);
    } catch { toast.error('Erro ao carregar pipeline'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openBaixaModal = async (lista) => {
    setBaixaModal(lista);
    setBaixaSelected([]);
    setBaixaForm({ empresa: 'SPC', observacao: '' });
    try {
      const res = await adminAPI.getLista(lista.id);
      setBaixaCases(res.data.items || []);
    } catch {}
  };

  const handleBaixaLote = async () => {
    if (!baixaSelected.length) { toast.error('Selecione clientes'); return; }
    setBaixaLoading(true);
    try {
      await adminAPI.baixaLote({ caseIds: baixaSelected, empresa: baixaForm.empresa, observacao: baixaForm.observacao });
      toast.success(`Baixa ${baixaForm.empresa} para ${baixaSelected.length} clientes!`);
      setBaixaModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setBaixaLoading(false); }
  };

  const handleProtocolar = async (listaId) => {
    if (!confirm('Protocolar esta lista? O numero sera gerado automaticamente.')) return;
    try {
      const res = await adminAPI.protocolarLista(listaId, {});
      toast.success(res.data.message || 'Lista PROTOCOLADA!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:28,height:28}} /></div>;
  }

  const columns = data?.columns || {};
  const metricas = data?.metricas || {};

  return (
    <div className="space-y-5" data-testid="admin-retornos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Retorno</h1>
          <p className="text-sm text-white/50">Pipeline operacional de listas</p>
        </div>
        <div className="flex items-center gap-4">
          {Object.entries(metricas).map(([k, v]) => (
            <div key={k} className="text-center">
              <p className="font-display text-lg font-bold" style={{color:'var(--rn-green)'}}>{v}</p>
              <p className="text-[9px] text-white/35">{k === 'enviadas' ? 'Enviadas' : k === 'aguardandoBaixas' ? 'Aguardando' : k === 'baixasAndamento' ? 'Em Andamento' : 'Concluidas'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pipeline Board */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {Object.entries(COLUMN_CONFIG).map(([key, config]) => {
          const items = columns[key] || [];
          const Icon = config.icon;
          return (
            <div key={key} className="rounded-2xl border p-4" style={{background: config.color, borderColor: config.border}}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} style={{color: config.iconColor}} />
                <h3 className="text-sm font-semibold text-white/80">{config.title}</h3>
                <span className="ml-auto text-[10px] font-mono text-white/40">{items.length}</span>
              </div>

              {items.length === 0 ? (
                <p className="text-[10px] text-white/25 text-center py-4">Nenhuma lista</p>
              ) : (
                <div className="space-y-2">
                  {items.map(lista => (
                    <div key={lista.id} className="rounded-xl p-3 border border-white/10 bg-[rgba(0,0,0,0.2)]">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-medium text-white/85 truncate max-w-[120px]">{lista.name}</p>
                        <span className="text-[9px] text-white/35">{lista.totalClients} cl.</span>
                      </div>

                      {/* Progress bar */}
                      {lista.statusNorm === 'protocolada' && (
                        <div className="mb-2">
                          <div className="flex items-center justify-between text-[9px] text-white/40 mb-0.5">
                            <span>Baixas</span>
                            <span>{lista.totalBaixadas}/{lista.totalEmpresas} ({lista.progresso}%)</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${lista.progresso}%`,
                              background: lista.progresso >= 100 ? 'var(--rn-green)' : 'var(--rn-warning)',
                              transition: 'width 0.5s ease',
                            }} />
                          </div>
                        </div>
                      )}

                      {lista.protocoloNumero && (
                        <p className="text-[9px] text-white/35">Prot: {lista.protocoloNumero}</p>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-1 mt-2 flex-wrap">
                        <button onClick={() => navigate(`/admin/returns/lote/${lista.id}`)} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/10 text-white/60 flex items-center gap-1">
                          <ExternalLink size={10} /> Abrir
                        </button>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-lists/${lista.id}/download-xlsx`} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/10" style={{color:'var(--rn-green)'}}>
                          XLSX
                        </a>
                        {key === 'ENVIADAS' && (
                          <button onClick={() => handleProtocolar(lista.id)} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/10" style={{color:'var(--rn-green)'}}>
                            Protocolar
                          </button>
                        )}
                        {(key === 'AGUARDANDO_BAIXAS' || key === 'BAIXAS_ANDAMENTO') && (
                          <button onClick={() => navigate(`/admin/returns/lote/${lista.id}`)} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/10" style={{color:'var(--rn-green)'}}>
                            Baixas
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Baixa Modal */}
      {baixaModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setBaixaModal(null)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 p-5 border-b border-white/10" style={{background:'rgba(17,24,23,0.95)',backdropFilter:'blur(12px)'}}>
              <h3 className="font-display text-base font-semibold">Registrar Baixas — {baixaModal.name}</h3>
              <p className="text-[10px] text-white/40">{baixaModal.totalClients} clientes</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-white/40 block mb-1">Empresa *</label>
                <select value={baixaForm.empresa} onChange={e => setBaixaForm(p=>({...p,empresa:e.target.value}))}
                  className="w-full bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]">
                  {EMPRESAS.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </div>
              <textarea value={baixaForm.observacao} onChange={e => setBaixaForm(p=>({...p,observacao:e.target.value}))} placeholder="Observacao (opcional)" rows={2}
                className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white/60">Clientes ({baixaCases.length})</p>
                  <button onClick={() => setBaixaSelected(baixaSelected.length === baixaCases.length ? [] : baixaCases.map(i => i.caseId))}
                    className="text-[10px] hover:underline" style={{color:'var(--rn-green)'}}>
                    {baixaSelected.length === baixaCases.length ? 'Desmarcar' : 'Todos'}
                  </button>
                </div>
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {baixaCases.map(item => (
                    <label key={item.caseId} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      baixaSelected.includes(item.caseId) ? 'bg-[rgba(99,203,182,0.06)]' : 'bg-white/[0.02]'
                    }`}>
                      <Checkbox checked={baixaSelected.includes(item.caseId)}
                        onCheckedChange={() => setBaixaSelected(p => p.includes(item.caseId) ? p.filter(x=>x!==item.caseId) : [...p, item.caseId])}
                        className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]" />
                      <span className="text-xs text-white/80">{item.user?.name || 'N/A'}</span>
                      <span className="text-[9px] font-mono text-white/35 ml-auto">{item.case?.docFormatted}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setBaixaModal(null)} className="btn-ghost flex-1 text-sm">Cancelar</button>
                <button onClick={handleBaixaLote} disabled={baixaLoading || !baixaSelected.length}
                  className="btn-primary flex-1 text-sm disabled:opacity-50">
                  {baixaLoading ? <span className="rn-spinner mx-auto" style={{width:14,height:14}} /> : `Baixa ${baixaForm.empresa} (${baixaSelected.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
